import { useMemo, useState, type ReactNode } from 'react';
import { Check, ChevronRight, Search, SlidersHorizontal, X } from 'lucide-react';
import { BottomSheet } from './BottomSheet';
import { CalendarPanel } from './DateField';
import type { SelectOption } from './Select';
import { useI18n } from '../../contexts/I18nContext';
import { fromDateKey } from '../../lib/calendarDates';

/**
 * Linear-style mobile filters: one "Filtre" trigger with an applied-count
 * badge that opens a bottom sheet listing all filters; tapping a filter type
 * opens a second stacked sheet with its options (searchable list) or a date
 * range picker. Desktop keeps its inline dropdowns — surfaces render this with
 * `className="sm:hidden"` next to them.
 *
 * Driven entirely by declarative descriptors so TasksView and CalendarToolbar
 * reuse the exact option arrays and setters their desktop controls already use.
 */
export type FilterDescriptor =
  | {
      kind: 'select';
      id: string;
      label: string;
      icon: ReactNode;
      value: string;
      onChange: (value: string) => void;
      options: SelectOption[];
      loading?: boolean;
      /** The "Toate…" row that clears the filter; also the grey value hint. */
      placeholder?: string;
      /**
       * The value that means "not filtered" when it is not the empty string —
       * e.g. the "All users" sentinel. Clearing sets it, and holding it does
       * not count toward the badge.
       */
      defaultValue?: string;
    }
  | {
      /** Several options at once (weekly status chips); empty = everything. */
      kind: 'multi-select';
      id: string;
      label: string;
      icon: ReactNode;
      values: string[];
      onChange: (values: string[]) => void;
      options: SelectOption[];
      /** The "Toate" row that clears the selection. */
      placeholder?: string;
    }
  | {
      /** One day (the daily summary's date). */
      kind: 'date';
      id: string;
      label: string;
      icon: ReactNode;
      value: string;
      onChange: (value: string) => void;
      /** The day that counts as "not filtered" — today, for the daily page. */
      defaultValue?: string;
    }
  | {
      kind: 'date-range';
      id: string;
      label: string;
      icon: ReactNode;
      from: string;
      to: string;
      onFromChange: (value: string) => void;
      onToChange: (value: string) => void;
    }
  | {
      /** Sort control: lives in the sheet but never counts toward the badge. */
      kind: 'sort';
      id: string;
      label: string;
      icon: ReactNode;
      value: string;
      onChange: (value: string) => void;
      options: SelectOption[];
      defaultValue: string;
    };

interface MobileFilterSheetProps {
  filters: FilterDescriptor[];
  onClearAll?: () => void;
  className?: string;
}

/** Whether a filter currently holds a non-default value (shows as applied). */
function isApplied(f: FilterDescriptor): boolean {
  switch (f.kind) {
    case 'select':
      return f.value !== (f.defaultValue ?? '');
    case 'multi-select':
      return f.values.length > 0;
    case 'date':
      return f.value !== (f.defaultValue ?? '');
    case 'date-range':
      return Boolean(f.from || f.to);
    case 'sort':
      return f.value !== f.defaultValue;
  }
}

export function MobileFilterSheet({ filters, onClearAll, className }: MobileFilterSheetProps) {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  // One search input: filters the type list on the base sheet, the option list
  // on a sub-sheet. Reset on every sheet transition.
  const [query, setQuery] = useState('');

  const appliedCount = filters.filter((f) => f.kind !== 'sort' && isApplied(f)).length;
  const applied = filters.filter(isApplied);
  const active = filters.find((f) => f.id === activeId) ?? null;

  const formatDate = (key: string) =>
    new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(fromDateKey(key));

  /** Short human value for a filter ("CCC Operations", "12 aug. – 20 aug."). */
  function valueLabel(f: FilterDescriptor): string {
    switch (f.kind) {
      case 'select':
      case 'sort': {
        const opt = f.options.find((o) => o.value === f.value);
        return opt?.label ?? '';
      }
      case 'multi-select':
        return f.options
          .filter((o) => f.values.includes(o.value))
          .map((o) => o.label)
          .join(', ');
      case 'date':
        return f.value ? formatDate(f.value) : '';
      case 'date-range': {
        const from = f.from ? formatDate(f.from) : '';
        const to = f.to ? formatDate(f.to) : '';
        if (from && to) return `${from} – ${to}`;
        if (from) return `≥ ${from}`;
        if (to) return `≤ ${to}`;
        return '';
      }
    }
  }

  function clearFilter(f: FilterDescriptor) {
    switch (f.kind) {
      case 'select':
        f.onChange(f.defaultValue ?? '');
        break;
      case 'multi-select':
        f.onChange([]);
        break;
      case 'date':
        f.onChange(f.defaultValue ?? '');
        break;
      case 'date-range':
        f.onFromChange('');
        f.onToChange('');
        break;
      case 'sort':
        f.onChange(f.defaultValue);
        break;
    }
  }

  function openSheet() {
    setQuery('');
    setActiveId(null);
    setOpen(true);
  }

  function closeAll() {
    setOpen(false);
    setActiveId(null);
    setQuery('');
  }

  function openSub(id: string) {
    setActiveId(id);
    setQuery('');
  }

  function closeSub() {
    setActiveId(null);
    setQuery('');
  }

  const q = query.trim().toLowerCase();
  const visibleTypes = q ? filters.filter((f) => f.label.toLowerCase().includes(q)) : filters;

  return (
    <div className={className}>
      {/* min-h mirrors the 44px touch-target the `(pointer: coarse)` rule
          gives buttons, so the trigger and the search input line up even in
          emulators without a coarse pointer. */}
      <button
        type="button"
        onClick={openSheet}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={
          appliedCount > 0
            ? `${t('mobileFilters.trigger')} — ${t('mobileFilters.appliedCount', { count: appliedCount })}`
            : t('mobileFilters.trigger')
        }
        className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        <SlidersHorizontal size={15} />
        {t('mobileFilters.trigger')}
        {appliedCount > 0 && (
          <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-600 px-1 text-xs font-semibold text-fixed-white">
            {appliedCount}
          </span>
        )}
      </button>

      {/* Base sheet */}
      <BottomSheet
        open={open}
        onClose={closeAll}
        ariaLabel={t('mobileFilters.title')}
        closeOnEscape={activeId === null}
      >
        <div className="flex items-center gap-2 border-b border-border px-4 py-2">
          <Search size={16} className="pointer-events-none shrink-0 text-gray-400" />
          <input
            value={activeId === null ? query : ''}
            onChange={(e) => setQuery(e.target.value)}
            // Not a form field: keep Chrome from guessing a card/name autofill.
            type="search"
            name="firmio-picker-search"
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
            placeholder={t('mobileFilters.searchAll')}
            className="w-full bg-transparent [&::-webkit-search-cancel-button]:hidden text-sm text-foreground placeholder:text-gray-400 focus:outline-none"
          />
          <button
            type="button"
            onClick={closeAll}
            aria-label={t('common.close')}
            className="touch-target-auto rounded p-1 text-gray-400 hover:bg-accent hover:text-foreground"
          >
            <X size={16} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-2">
          {applied.length > 0 && (
            <div className="border-b border-border px-2 py-2">
              <p className="px-2 pb-1 text-xs font-medium uppercase tracking-wide text-gray-400">
                {t('mobileFilters.applied')}
              </p>
              {applied.map((f) => {
                const selectedOpt =
                  f.kind === 'select' || f.kind === 'sort'
                    ? f.options.find((o) => o.value === f.value)
                    : undefined;
                return (
                  <div key={f.id} className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => openSub(f.id)}
                      className="ui-menu-item min-w-0 flex-1 rounded"
                    >
                      <span className="shrink-0 text-gray-400">{f.icon}</span>
                      <span className="min-w-0 truncate">
                        {f.label}{' '}
                        <span className="text-gray-400">
                          {f.kind === 'date-range' ? '' : t('mobileFilters.is')}
                        </span>{' '}
                        <span className="inline-flex max-w-full items-center gap-1.5 rounded bg-accent px-1.5 py-0.5 font-medium text-accent-foreground">
                          {selectedOpt?.color && (
                            <span
                              className="inline-block h-2 w-2 shrink-0 rounded-full"
                              style={{ backgroundColor: selectedOpt.color }}
                            />
                          )}
                          <span className="truncate">{valueLabel(f)}</span>
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => clearFilter(f)}
                      aria-label={`${t('mobileFilters.remove')}: ${f.label}`}
                      className="touch-target-auto shrink-0 rounded p-2 text-gray-400 hover:bg-accent hover:text-foreground"
                    >
                      <X size={15} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="px-2 py-2">
            <p className="px-2 pb-1 text-xs font-medium uppercase tracking-wide text-gray-400">
              {t('mobileFilters.all')}
            </p>
            {visibleTypes.length === 0 && (
              <p className="px-2 py-2 text-sm text-gray-400">{t('ui.select.noResults')}</p>
            )}
            {visibleTypes.map((f) => {
              const current = valueLabel(f);
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => openSub(f.id)}
                  className="ui-menu-item justify-between rounded"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="shrink-0 text-gray-400">{f.icon}</span>
                    <span className="truncate">{f.label}</span>
                  </span>
                  <span className="flex min-w-0 shrink-0 items-center gap-1 text-gray-400">
                    {current && <span className="max-w-32 truncate text-xs">{current}</span>}
                    <ChevronRight size={15} />
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {onClearAll && appliedCount > 0 && (
          <div className="border-t border-border px-4 py-2">
            <button
              type="button"
              onClick={() => {
                onClearAll();
                closeAll();
              }}
              className="w-full rounded px-2 py-2 text-sm font-medium text-blue-600 hover:bg-accent"
            >
              {t('tasks.clearFilters')}
            </button>
          </div>
        )}
      </BottomSheet>

      {/* Sub-sheet: its own portal stacked over the base one, with its own
          slide-up — never a transform on a shared ancestor (breaks iOS fixed). */}
      {active && (
        <FilterSubSheet
          key={active.id}
          filter={active}
          query={query}
          onQueryChange={setQuery}
          onClose={closeSub}
        />
      )}
    </div>
  );
}

function FilterSubSheet({
  filter,
  query,
  onQueryChange,
  onClose,
}: {
  filter: FilterDescriptor;
  query: string;
  onQueryChange: (q: string) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();

  return (
    <BottomSheet open onClose={onClose} ariaLabel={filter.label} zIndex={60}>
      {filter.kind === 'date-range' ? (
        <DateRangeBody filter={filter} onDone={onClose} />
      ) : filter.kind === 'date' ? (
        <DateBody filter={filter} onDone={onClose} />
      ) : (
        <OptionListBody
          filter={filter}
          query={query}
          onQueryChange={onQueryChange}
          onClose={onClose}
        />
      )}
      {filter.kind !== 'date-range' && filter.kind !== 'date' && (
        <div className="border-t border-border px-4 py-2">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded px-2 py-2 text-sm font-medium text-gray-600 hover:bg-accent"
          >
            {t('mobileFilters.back')}
          </button>
        </div>
      )}
    </BottomSheet>
  );
}

function OptionListBody({
  filter,
  query,
  onQueryChange,
  onClose,
}: {
  filter: Extract<FilterDescriptor, { kind: 'select' | 'sort' | 'multi-select' }>;
  query: string;
  onQueryChange: (q: string) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const q = query.trim().toLowerCase();
  const options = useMemo(
    () =>
      q
        ? filter.options.filter(
            (o) =>
              o.label.toLowerCase().includes(q) ||
              (o.description ? o.description.toLowerCase().includes(q) : false),
          )
        : filter.options,
    [filter.options, q],
  );

  // A multi-select stays open: picking several statuses one sheet-trip at a
  // time would be three times the taps for the common case.
  function pick(value: string) {
    if (filter.kind === 'multi-select') {
      filter.onChange(
        filter.values.includes(value)
          ? filter.values.filter((v) => v !== value)
          : [...filter.values, value],
      );
      return;
    }
    filter.onChange(value);
    onClose();
  }

  function clear() {
    if (filter.kind === 'multi-select') filter.onChange([]);
    else if (filter.kind === 'select') filter.onChange(filter.defaultValue ?? '');
    if (filter.kind !== 'multi-select') onClose();
  }

  const isSelected = (value: string) =>
    filter.kind === 'multi-select' ? filter.values.includes(value) : value === filter.value;
  const nothingSelected =
    filter.kind === 'multi-select'
      ? filter.values.length === 0
      : filter.value === (filter.kind === 'select' ? (filter.defaultValue ?? '') : '');

  const loading = filter.kind === 'select' && filter.loading;

  return (
    <>
      <div className="flex items-center gap-2 border-b border-border px-4 py-2">
        <Search size={16} className="pointer-events-none shrink-0 text-gray-400" />
        <input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          // Not a form field: keep Chrome from guessing a card/name autofill.
          type="search"
          name="firmio-picker-search"
          autoComplete="off"
          data-1p-ignore
          data-lpignore="true"
          placeholder={filter.label}
          className="w-full bg-transparent [&::-webkit-search-cancel-button]:hidden text-sm text-foreground placeholder:text-gray-400 focus:outline-none"
        />
        <button
          type="button"
          onClick={onClose}
          aria-label={t('mobileFilters.back')}
          className="touch-target-auto rounded p-1 text-gray-400 hover:bg-accent hover:text-foreground"
        >
          <X size={16} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1">
        {loading && <p className="px-4 py-2 text-sm text-gray-400">{t('ui.select.loading')}</p>}
        {!loading && filter.kind !== 'sort' && filter.placeholder && !q && (
          <button type="button" onClick={clear} className="ui-menu-item justify-between">
            <span className="truncate text-gray-500">{filter.placeholder}</span>
            {nothingSelected && <Check size={15} className="shrink-0 text-gray-400" />}
          </button>
        )}
        {!loading && options.length === 0 && (
          <p className="px-4 py-2 text-sm text-gray-400">{t('ui.select.noResults')}</p>
        )}
        {!loading &&
          options.map((opt) => {
            const isOn = isSelected(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                disabled={opt.disabled}
                onClick={() => pick(opt.value)}
                className={`ui-menu-item justify-between ${
                  opt.disabled ? 'cursor-not-allowed opacity-50 hover:bg-transparent' : ''
                }`}
              >
                <span className="flex min-w-0 items-center gap-2">
                  {opt.color && (
                    <span
                      className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: opt.color }}
                    />
                  )}
                  {opt.icon && <span className="shrink-0">{opt.icon}</span>}
                  <span className="truncate">{opt.label}</span>
                </span>
                {isOn && <Check size={15} className="shrink-0 text-gray-400" />}
              </button>
            );
          })}
      </div>
    </>
  );
}

function DateBody({
  filter,
  onDone,
}: {
  filter: Extract<FilterDescriptor, { kind: 'date' }>;
  onDone: () => void;
}) {
  return (
    <>
      <div className="border-b border-border px-4 py-2">
        <p className="text-sm font-medium text-foreground">{filter.label}</p>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <CalendarPanel
          value={filter.value}
          onPick={(key) => {
            filter.onChange(key);
            onDone();
          }}
        />
      </div>
    </>
  );
}

function DateRangeBody({
  filter,
  onDone,
}: {
  filter: Extract<FilterDescriptor, { kind: 'date-range' }>;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const [end, setEnd] = useState<'from' | 'to'>('from');

  const segment = (target: 'from' | 'to', label: string, value: string) => (
    <button
      type="button"
      onClick={() => setEnd(target)}
      aria-pressed={end === target}
      className={`flex-1 rounded px-2.5 py-1.5 text-sm ${
        end === target
          ? 'bg-gray-100 font-medium text-gray-900'
          : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      {label}
      {value ? <span className="ml-1 text-xs text-gray-400">{value}</span> : null}
    </button>
  );

  return (
    <>
      <div className="border-b border-border px-4 py-2">
        <p className="pb-2 text-sm font-medium text-foreground">{filter.label}</p>
        <div className="flex items-center gap-1 rounded border border-gray-300 p-0.5">
          {segment('from', t('mobileFilters.rangeFrom'), filter.from)}
          {segment('to', t('mobileFilters.rangeTo'), filter.to)}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {/* Remount per end so the month cursor follows the edited value. */}
        {end === 'from' ? (
          <CalendarPanel
            key="from"
            value={filter.from}
            max={filter.to || undefined}
            onPick={filter.onFromChange}
          />
        ) : (
          <CalendarPanel
            key="to"
            value={filter.to}
            min={filter.from || undefined}
            onPick={filter.onToChange}
          />
        )}
      </div>

      <div className="border-t border-border px-4 py-2">
        <button
          type="button"
          onClick={onDone}
          className="w-full rounded bg-brand-600 px-2 py-2 text-sm font-medium text-fixed-white hover:bg-brand-700"
        >
          {t('mobileFilters.done')}
        </button>
      </div>
    </>
  );
}
